import { SortDirection } from '@/enums/sort';
import { Sort } from '@/types/sort.type';
import { useEffect, useMemo, useState } from 'react';

export const sortGroupTableValuesByDirection = (
    leftValue: string | number | null,
    rightValue: string | number | null,
    direction: SortDirection,
): number => {
    if (leftValue === rightValue) {
        return 0;
    }

    if (leftValue === null) {
        return 1;
    }

    if (rightValue === null) {
        return -1;
    }

    const multiplier = direction === SortDirection.DESC ? -1 : 1;

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * multiplier;
    }

    return (
        String(leftValue).localeCompare(String(rightValue), 'ru', { sensitivity: 'base', numeric: true }) * multiplier
    );
};

type UseGroupTableViewModelCoreArgs<TData> = {
    data: TData[];
    page: number;
    rowsOnPage: number;
    setPage: (page: number) => void;
    setPageCount: (count: number) => void;
    setTotalRows: (count: number) => void;
    filterData: (item: TData) => boolean;
    sortData: (items: TData[], sortKey: keyof TData, direction: SortDirection) => TData[];
};

export const useGroupTableViewModelCore = <TData>({
    data,
    page,
    rowsOnPage,
    setPage,
    setPageCount,
    setTotalRows,
    filterData,
    sortData,
}: UseGroupTableViewModelCoreArgs<TData>) => {
    const [sort, setSort] = useState<Sort>({});

    const filteredAndSortedData = useMemo(() => {
        const filteredData = data.filter(filterData);
        const sortDirection = sort.sortOrder;

        if (!sort.sortKey || !sortDirection) {
            return filteredData;
        }

        return sortData(filteredData, sort.sortKey as keyof TData, sortDirection);
    }, [data, filterData, sort.sortKey, sort.sortOrder, sortData]);

    const pageCount = useMemo(() => {
        if (!filteredAndSortedData.length) {
            return 1;
        }

        return Math.ceil(filteredAndSortedData.length / rowsOnPage);
    }, [filteredAndSortedData.length, rowsOnPage]);

    useEffect(() => {
        setPageCount(pageCount);
    }, [pageCount, setPageCount]);

    useEffect(() => {
        setTotalRows(filteredAndSortedData.length);
    }, [filteredAndSortedData.length, setTotalRows]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount, setPage]);

    const pagedData = useMemo(() => {
        const start = (page - 1) * rowsOnPage;
        return filteredAndSortedData.slice(start, start + rowsOnPage);
    }, [filteredAndSortedData, page, rowsOnPage]);

    const onSortChanged = (column: keyof TData, direction?: SortDirection) => {
        setSort({
            sortKey: direction && direction !== SortDirection.NONE ? String(column) : undefined,
            sortOrder: direction && direction !== SortDirection.NONE ? direction : undefined,
        });
    };

    return {
        sort,
        filteredAndSortedData,
        pagedData,
        onSortChanged,
    };
};
